import { RegisterUserRequirementsRequest } from 'gportal-grpc/gportal/api/user/v1/requests_pb';
import { RegisterUserRequirementsResponseCountryOption } from 'gportal-grpc/gportal/api/user/v1/responses_pb';
import useLocaleObject from '~/composables/useLocaleObject';
import { GpRegion } from '~/plugins/grpc/universal/types';
import type { RegisterUserFormData } from '../types';
import type { FilterSelectItemsFn } from '~/components/form/select/types';

export default async (formData: RegisterUserFormData) => {
  const localeObject = useLocaleObject();

  const currencyStore = useCurrencyStore();
  const currentCurrency = computed<string>(() => currencyStore.currency);

  const { $sentry } = useNuxtApp();
  const $grpcUser = useGrpcUser();
  const { t, locale } = useI18n();
  const { status, refresh, data } = await useAsyncData(async () => {
    const request = new RegisterUserRequirementsRequest();
    request.locale = locale.value;

    const { response, error } = await $grpcUser.executeApiCallPrimary(
      'registerUserRequirements',
      request,
    );

    if (error == null && response != null) {
      let presetCountryCode = localeObject.value.iso.split('-')[1];

      if (localeObject.value.code === 'en') {
        if (currentCurrency.value === 'GBP') {
          presetCountryCode = 'GB';
        } else if ($grpcUser.primaryRegion === GpRegion.INT) {
          presetCountryCode = 'US';
        } else {
          presetCountryCode = 'DE';
        }
      }

      const { code: countryCode, name } = response.countryOptions.find(
        ({ code }) => code === presetCountryCode,
      );

      formData.country = countryCode;

      return {
        countriesRequiringFullAddress: response.countriesRequiringFullAddress,
        countriesRequiringPostalCode: response.countriesRequiringPostalCode,
        countryOptionsList: response.countryOptions.map((c) => ({
          code: c.code,
          name: c.name,
        })),
      };
    }
    $sentry.captureException(error);
    throw error;
  });

  const countriesRequiringFullAddress = computed<string[]>(
    () => data.value.countriesRequiringFullAddress,
  );

  const countriesRequiringPostalCode = computed<string[]>(
    () => data.value.countriesRequiringPostalCode,
  );

  const availableCountries = computed<
    {
      code: string;
      name: string;
    }[]
  >(() => data.value.countryOptionsList);

  // country handling
  const handleFilterCountries: FilterSelectItemsFn<{
    code: string;
    name: string;
  }> = (items, query) => {
    if (query == null || query.length === 0) return Promise.resolve(items);

    return Promise.resolve(
      items.filter(
        ({ code, name }) =>
          name.toLowerCase().indexOf(query.toLowerCase()) >= 0 ||
          code.toLowerCase() === query.toLowerCase(),
      ),
    );
  };

  const isCountryZipRequired = computed<boolean>(() =>
    countriesRequiringPostalCode.value.some(
      (country) => country === formData.country,
    ),
  );

  const isCountryFullAddressRequired = computed<boolean>(() =>
    countriesRequiringFullAddress.value.some(
      (country) => country === formData.country,
    ),
  );

  return {
    availableCountries,
    isCountryFullAddressRequired,
    isCountryZipRequired,
    countriesFetchState: status,
    fetchCountries: refresh,
    handleFilterCountries,
  };
};
